import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  MdAdd,
  MdEdit,
  MdDelete,
  MdSearch,
  MdOutlineCheckCircle,
  MdOutlineRemoveCircleOutline,
  MdCloudUpload,
  MdClose,
} from "react-icons/md";
import {
  Utils,
  authJsonHeaders,
  handleSessionExpired,
  PARTNER_TYPE,
  PARTNER_CREDENTIAL_FIELD_TYPE,
  PARTNER_HTTP_METHOD,
  DEFAULT_STATUS,
  slugify,
  selectStyles,
  PartnerLogo,
  getDefaultPageSize,
} from "../../utils";
import { useActionDropdown } from "../../hooks/useActionDropdown";
import Modal from "../common/Modal";
import ListPaginationBar from "../common/ListPaginationBar";

const FIELD_HEIGHT = 42;

const inputStyle = {
  width: "100%",
  height: FIELD_HEIGHT,
  padding: "0 12px",
  border: "1px solid #d1d5db",
  borderRadius: "8px",
  fontSize: "14px",
  boxSizing: "border-box",
};

const textareaStyle = {
  ...inputStyle,
  height: "auto",
  minHeight: 60,
  padding: 12,
  resize: "vertical",
};

const fieldSelectStyles = {
  ...selectStyles,
  control: (base, state) => ({
    ...selectStyles.control(base, state),
    minHeight: FIELD_HEIGHT,
    height: FIELD_HEIGHT,
    borderRadius: 8,
    borderColor: state.isFocused ? "#4f46e5" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 1px #4f46e5" : "none",
    "&:hover": { borderColor: state.isFocused ? "#4f46e5" : "#9ca3af" },
  }),
  valueContainer: (base) => ({
    ...base,
    height: FIELD_HEIGHT - 2,
    padding: "0 10px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: FIELD_HEIGHT - 2,
  }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
};

const sectionBox = {
  gridColumn: "1 / -1",
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  padding: 14,
  background: "#fafafa",
};

const btnPrimary = {
  padding: "8px 14px",
  height: 38,
  background: "#4f46e5",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: "13px",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  boxSizing: "border-box",
};

const btnGhost = {
  padding: "10px 18px",
  background: "#f3f4f6",
  color: "#374151",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: 500,
  fontSize: "14px",
};

const partnerTypeOptions = PARTNER_TYPE.map((t) => ({ value: t.value, label: t.label }));

const typeFilterOptions = [{ value: "all", label: "All types" }, ...partnerTypeOptions];
const statusFilterOptions = [
  { value: "all", label: "All statuses" },
  ...DEFAULT_STATUS.map((s) => ({ value: s.value, label: s.label })),
];

const filterSelectStyles = {
  ...fieldSelectStyles,
  control: (base, state) => ({
    ...fieldSelectStyles.control(base, state),
    minWidth: 160,
  }),
};

const emptyPartnerField = () => ({
  key: "",
  label: "",
  type: PARTNER_CREDENTIAL_FIELD_TYPE[0].value,
  value: "",
});

const emptyEndpoint = () => ({
  key: "",
  label: "",
  path: "",
  http_method: PARTNER_HTTP_METHOD[0].value,
  param_keys: [],
  body_param_keys: [],
});

function buildEndpointPreviewBody(endpoint) {
  const keys = (endpoint?.body_param_keys || []).filter(Boolean);
  if (!keys.length) return "";
  return keys.map((k) => `${k}={${k}}`).join("&");
}

function joinPartnerBasePath(baseUrl, path) {
  const base = String(baseUrl || "").trim().replace(/\/+$/, "");
  const p = String(path || "").trim();
  if (!base) return p.startsWith("/") ? p : `/${p}`;
  if (!p) return base;
  return p.startsWith("/") ? `${base}${p}` : `${base}/${p}`;
}

function buildEndpointPreviewUrl(baseUrl, endpoint) {
  const path = String(endpoint?.path || "").trim();
  if (!path) return "";
  let url = joinPartnerBasePath(baseUrl, path);
  const keys = (endpoint?.param_keys || []).filter(Boolean);
  if (!keys.length) return url;
  const qs = keys.map((k) => `${k}={${k}}`).join("&");
  return `${url}${url.includes("?") ? "&" : "?"}${qs}`;
}

function normalizeEndpointForForm(row) {
  return {
    key: String(row?.key || ""),
    label: String(row?.label || ""),
    path: String(row?.path || ""),
    http_method: String(row?.http_method).trim().toUpperCase() || "GET",
    param_keys: Array.isArray(row?.param_keys)
      ? row.param_keys.map((k) => String(k || "").trim()).filter(Boolean)
      : [],
    body_param_keys: Array.isArray(row?.body_param_keys)
      ? row.body_param_keys.map((k) => String(k || "").trim()).filter(Boolean)
      : [],
  };
}

function normalizePartnerField(row) {
  return {
    key: String(row?.key || ""),
    label: String(row?.label || ""),
    type: String(row?.type ?? PARTNER_CREDENTIAL_FIELD_TYPE[0].value),
    value: String(row?.value ?? ""),
  };
}

function partnerFieldsFromRow(row, cfg) {
  const credentials = Array.isArray(cfg.credentials) ? cfg.credentials.map(normalizePartnerField) : [];
  return credentials.filter((c) => c.key);
}

const emptyDefaultConfig = () => ({
  endpoints: [],
  partner_events: "",
});

const emptyForm = {
  name: "",
  slug: "",
  partner_type: 1,
  description: "",
  about_text: "",
  logo_url: "",
  stat_label: "",
  base_url: "",
  partner_fields: [],
  default_config: emptyDefaultConfig(),
};

function formFromRow(row) {
  const cfg =
    row?.default_config && typeof row.default_config === "object" && !Array.isArray(row.default_config)
      ? row.default_config
      : {};
  let endpoints = Array.isArray(cfg.endpoints) ? cfg.endpoints.map(normalizeEndpointForForm) : [];
  return {
    ...emptyForm,
    name: row.name || "",
    slug: row.slug || "",
    partner_type: Number(row.partner_type) || 1,
    description: row.description || "",
    about_text: row.about_text || "",
    logo_url: row.logo_url || "",
    stat_label: row.stat_label || "",
    base_url: row.base_url || "",
    partner_fields: partnerFieldsFromRow(row, cfg),
    default_config: {
      endpoints,
      partner_events: Array.isArray(cfg.partner_events)
        ? cfg.partner_events
            .map((o) => (typeof o === "object" ? o.value || o.label : o))
            .filter((x) => x != null && String(x).trim())
            .join(", ")
        : String(cfg.partner_events || ""),
    },
  };
}

export default function AdminPartners() {
  const pageSize = getDefaultPageSize();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [pagination, setPagination] = useState({ page: 1, limit: pageSize, total: 0, totalPages: 0 });
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [slugLocked, setSlugLocked] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const tableScrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const getPartnerActionOptions = (row, { onEdit, onToggleStatus, onDelete } = {}) => [
    {
      key: "edit",
      label: "Edit",
      icon: <MdEdit size={18} />,
      action: () => onEdit?.(row),
    },
    {
      key: "toggle_status",
      label: Number(row?.status) === 1 ? "Deactivate" : "Activate",
      icon:
        Number(row?.status) === 1 ? (
          <MdOutlineRemoveCircleOutline size={18} />
        ) : (
          <MdOutlineCheckCircle size={18} />
        ),
      action: () => onToggleStatus?.(row),
    },
    {
      key: "delete",
      label: "Delete",
      icon: <MdDelete size={18} />,
      danger: true,
      action: () => onDelete?.(row),
    },
  ];

  const {
    openActionItem,
    dropdownPosition,
    dropdownRef,
    closeDropdown,
    handleDropdownAction,
    handleOpenDropdown,
  } = useActionDropdown({
    tableScrollRef,
    optionCount: (row) =>
      getPartnerActionOptions(row, {
        onEdit: () => {},
        onToggleStatus: () => {},
        onDelete: () => {},
      }).length,
    menuWidth: 180,
    useAnchored: true,
  });

  const typeFilterValue =
    typeFilterOptions.find((o) => String(o.value) === String(typeFilter)) || typeFilterOptions[0];
  const statusFilterValue =
    statusFilterOptions.find((o) => String(o.value) === String(statusFilter)) ||
    statusFilterOptions[0];

  const formPreviewPartner = useMemo(
    () => ({
      name: form.name,
      slug: form.slug,
      id: editing?.id,
      logo_url: logoPreview || form.logo_url,
    }),
    [form.name, form.slug, form.logo_url, logoPreview, editing?.id]
  );

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, search, typeFilter, statusFilter]);

  useEffect(() => {
    return () => {
      if (logoPreview && String(logoPreview).startsWith("blob:")) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  async function fetchRows() {
    setLoading(true);
    try {
      const response = await fetch(Utils.baseurl + "admin/partners", {
        method: "POST",
        headers: authJsonHeaders(Utils.storageKeys.adminToken),
        body: JSON.stringify({
          page: pagination.page,
          limit: pagination.limit || pageSize,
          search,
          partner_type: typeFilter,
          status: statusFilter,
        }),
      });
      const result = await response.json();
      if (handleSessionExpired(response, result)) return;
      if (result.status) {
        setRows(result.data || []);
        setPagination((p) => ({
          ...p,
          ...(result.pagination || {}),
          limit: result.pagination?.limit || p.limit,
        }));
      } else toast.error(result.message || "Failed to load partners");
    } catch (e) {
      console.error(e);
      toast.error("Failed to load partners");
    }
    setLoading(false);
  }

  function resetLogoLocal() {
    if (logoPreview && String(logoPreview).startsWith("blob:")) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoFile(null);
    setLogoPreview("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function openCreate() {
    closeDropdown();
    setEditing(null);
    setForm({ ...emptyForm, partner_fields: [], default_config: emptyDefaultConfig() });
    setSlugLocked(false);
    resetLogoLocal();
    setShowModal(true);
  }

  function openEdit(row) {
    closeDropdown();
    setEditing(row);
    setForm(formFromRow(row));
    setSlugLocked(true);
    resetLogoLocal();
    setShowModal(true);
  }

  function onNameChange(name) {
    setForm((prev) => ({
      ...prev,
      name,
      slug: slugLocked ? prev.slug : slugify(name, { maxLength: 64 }),
    }));
  }

  function onLogoFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    applyLogoFile(file);
    e.target.value = "";
  }

  function applyLogoFile(file) {
    if (!/\.(jpe?g|png|webp)$/i.test(file.name) && !/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      toast.error("Only JPEG, PNG and WebP images are allowed");
      return;
    }
    if (logoPreview && String(logoPreview).startsWith("blob:")) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  }

  function onLogoDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file) applyLogoFile(file);
  }

  function clearLogo() {
    resetLogoLocal();
    setForm((prev) => ({ ...prev, logo_url: "" }));
  }

  function updatePartnerField(idx, patch) {
    setForm((prev) => {
      const next = [...(prev.partner_fields || [])];
      const row = { ...next[idx], ...patch };
      if (patch.label != null && !String(next[idx]?.key || "").trim()) {
        row.key = slugify(patch.label, { maxLength: 64 }).replace(/-/g, "_");
      }
      next[idx] = row;
      return { ...prev, partner_fields: next };
    });
  }

  function addPartnerField() {
    setForm((prev) => ({
      ...prev,
      partner_fields: [...(prev.partner_fields || []), emptyPartnerField()],
    }));
  }

  function removePartnerField(idx) {
    setForm((prev) => ({
      ...prev,
      partner_fields: (prev.partner_fields || []).filter((_, i) => i !== idx),
    }));
  }

  function setDefaultConfigField(key, value) {
    setForm((prev) => ({
      ...prev,
      default_config: { ...prev.default_config, [key]: value },
    }));
  }

  function updateEndpoint(idx, patch) {
    setForm((prev) => {
      const next = [...(prev.default_config?.endpoints || [])];
      const row = { ...next[idx], ...patch };
      if (patch.label != null && !String(next[idx]?.key || "").trim()) {
        row.key = slugify(patch.label, { maxLength: 64 }).replace(/-/g, "_");
      }
      next[idx] = row;
      return {
        ...prev,
        default_config: { ...prev.default_config, endpoints: next },
      };
    });
  }

  function addEndpoint() {
    setForm((prev) => ({
      ...prev,
      default_config: {
        ...prev.default_config,
        endpoints: [...(prev.default_config?.endpoints || []), emptyEndpoint()],
      },
    }));
  }

  function removeEndpoint(idx) {
    setForm((prev) => ({
      ...prev,
      default_config: {
        ...prev.default_config,
        endpoints: (prev.default_config?.endpoints || []).filter((_, i) => i !== idx),
      },
    }));
  }

  const fieldKeyOptions = useMemo(() => {
    const byKey = new Map();
    (form.partner_fields || []).forEach((f) => {
      const key = String(f.key || "").trim();
      if (key) byKey.set(key, { value: key, label: f.label || key });
    });
    (form.default_config?.endpoints || []).forEach((ep) => {
      [...(ep.param_keys || []), ...(ep.body_param_keys || [])].forEach((k) => {
        const key = String(k || "").trim();
        if (key && !byKey.has(key)) byKey.set(key, { value: key, label: key });
      });
    });
    return [...byKey.values()];
  }, [form.partner_fields, form.default_config?.endpoints]);

  const multiSelectStyles = useMemo(
    () => ({
      ...fieldSelectStyles,
      control: (base, state) => ({
        ...fieldSelectStyles.control(base, state),
        minHeight: FIELD_HEIGHT,
        height: "auto",
      }),
      valueContainer: (base) => ({
        ...base,
        minHeight: FIELD_HEIGHT - 2,
        height: "auto",
        padding: "4px 10px",
      }),
    }),
    []
  );

  async function uploadLogoIfNeeded() {
    if (!logoFile) return form.logo_url || null;
    const fd = new FormData();
    fd.append("logo", logoFile);
    const headers = authJsonHeaders(Utils.storageKeys.adminToken);
    delete headers["Content-Type"];
    const response = await fetch(Utils.baseurl + "admin/partners/upload-logo", {
      method: "POST",
      headers,
      body: fd,
    });
    const result = await response.json();
    if (handleSessionExpired(response, result)) return null;
    if (!result.status || !result.path) {
      throw new Error(result.message || "Logo upload failed");
    }
    return result.path;
  }

  async function handleSave(e) {
    e?.preventDefault?.();
    if (!String(form.name || "").trim()) {
      toast.error("Name is required");
      return;
    }
    const partner_fields = (form.partner_fields || [])
      .map((f) => ({
        key: String(f.key || "").trim(),
        label: String(f.label || "").trim(),
        type: String(f.type ?? ""),
        value: String(f.value ?? ""),
      }))
      .filter((f) => f.key && f.label);

    const dc = form.default_config || emptyDefaultConfig();
    const endpoints = (dc.endpoints || [])
      .map((r) => ({
        key: String(r.key || "").trim(),
        label: String(r.label || "").trim(),
        path: String(r.path || "").trim(),
        http_method: String(r.http_method).trim().toUpperCase(),
        param_keys: (r.param_keys || []).map((k) => String(k || "").trim()).filter(Boolean),
        body_param_keys: (r.body_param_keys || []).map((k) => String(k || "").trim()).filter(Boolean),
      }))
      .filter((r) => r.key && r.path);

    const default_config = {
      credentials: partner_fields,
      endpoints,
      partner_events: String(dc.partner_events || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean),
    };

    setSaving(true);
    try {
      const logo_url = await uploadLogoIfNeeded();
      const response = await fetch(Utils.baseurl + "admin/partners/save", {
        method: "POST",
        headers: authJsonHeaders(Utils.storageKeys.adminToken),
        body: JSON.stringify({
          id: editing?.id || undefined,
          name: form.name,
          slug: form.slug,
          partner_type: form.partner_type,
          description: form.description,
          about_text: form.about_text,
          logo_url: logo_url || null,
          stat_label: form.stat_label,
          base_url: form.base_url,
          default_config,
        }),
      });
      const result = await response.json();
      if (handleSessionExpired(response, result)) return;
      if (result.status) {
        toast.success(result.message || "Saved");
        setShowModal(false);
        fetchRows();
      } else {
        toast.error(result.message || "Save failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Save failed");
    }
    setSaving(false);
  }

  async function handleToggleStatus(row) {
    closeDropdown();
    const nextStatus = Number(row.status) === 1 ? 0 : 1;
    try {
      const response = await fetch(Utils.baseurl + "admin/partners/status", {
        method: "POST",
        headers: authJsonHeaders(Utils.storageKeys.adminToken),
        body: JSON.stringify({ id: row.id, status: nextStatus }),
      });
      const result = await response.json();
      if (handleSessionExpired(response, result)) return;
      if (result.status) {
        toast.success(result.message || (nextStatus === 1 ? "Activated" : "Deactivated"));
        fetchRows();
      } else {
        toast.error(result.message || "Failed to update status");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    }
  }

  async function handleDelete(row) {
    closeDropdown();
    const confirm = await Swal.fire({
      title: "Delete partner?",
      text: row.name,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!confirm.isConfirmed) return;
    try {
      const response = await fetch(Utils.baseurl + "admin/partners/delete", {
        method: "POST",
        headers: authJsonHeaders(Utils.storageKeys.adminToken),
        body: JSON.stringify({ id: row.id }),
      });
      const result = await response.json();
      if (handleSessionExpired(response, result)) return;
      if (result.status) {
        toast.success(result.message || "Deleted");
        fetchRows();
      } else {
        toast.error(result.message || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Delete failed");
    }
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24 }}>Partner Integrations</h1>
          <p style={{ margin: "6px 0 0", color: "#6b7280", fontSize: 14 }}>
            Marketplace catalog — apps integrate these partners under Collaborate.
          </p>
        </div>
        <button type="button" style={btnPrimary} onClick={openCreate}>
          <MdAdd size={16} /> Add partner
        </button>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <div style={{ position: "relative", flex: "1 1 220px", maxWidth: 320 }}>
          <MdSearch
            size={18}
            aria-hidden
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#9ca3af",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
          <input
            style={{ ...inputStyle, paddingLeft: 40 }}
            placeholder="Search partners"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
          />
        </div>
        <div style={{ minWidth: 160, width: 180 }}>
          <Select
            inputId="admin-partners-type"
            instanceId="admin-partners-type"
            classNamePrefix="ns-select"
            isSearchable={false}
            options={typeFilterOptions}
            value={typeFilterValue}
            onChange={(opt) => {
              setTypeFilter(opt?.value ?? "all");
              setPagination((p) => ({ ...p, page: 1 }));
            }}
            styles={filterSelectStyles}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
            placeholder="Type"
          />
        </div>
        <div style={{ minWidth: 160, width: 180 }}>
          <Select
            inputId="admin-partners-status"
            instanceId="admin-partners-status"
            classNamePrefix="ns-select"
            isSearchable={false}
            options={statusFilterOptions}
            value={statusFilterValue}
            onChange={(opt) => {
              setStatusFilter(opt?.value ?? "all");
              setPagination((p) => ({ ...p, page: 1 }));
            }}
            styles={filterSelectStyles}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
            placeholder="Status"
          />
        </div>
      </div>

      <div
        ref={tableScrollRef}
        style={{
          background: "#fff",
          borderRadius: 12,
          border: "1px solid #e5e7eb",
          overflow: "auto",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ background: "#f9fafb", textAlign: "left" }}>
              <th style={{ padding: 12 }}>Partner</th>
              <th style={{ padding: 12 }}>Type</th>
              <th style={{ padding: 12 }}>Status</th>
              <th style={{ padding: 12 }}>Slug</th>
              <th style={{ padding: 12, textAlign: "center" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>
                  No partners yet. Add StrongMetrics or another partner.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const typeLabel =
                  PARTNER_TYPE.find((x) => x.value === Number(row.partner_type))?.label || "—";
                const statusEntry =
                  DEFAULT_STATUS.find((x) => x.value === Number(row.status)) || DEFAULT_STATUS[1];
                return (
                  <tr key={row.id} style={{ borderTop: "1px solid #f3f4f6" }}>
                    <td style={{ padding: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <PartnerLogo partner={row} />
                        <strong>{row.name}</strong>
                      </div>
                    </td>
                    <td style={{ padding: 12 }}>{typeLabel}</td>
                    <td style={{ padding: 12 }}>
                      <span className={statusEntry.badgeClass}>{statusEntry.label}</span>
                    </td>
                    <td style={{ padding: 12, fontFamily: "monospace", fontSize: 12 }}>{row.slug}</td>
                    <td style={{ padding: 12, textAlign: "center" }} className="col-action">
                      <button
                        type="button"
                        className="dropdown-trigger-btn btn btn-link p-0"
                        onClick={(e) => handleOpenDropdown(e, row.id, row)}
                        aria-label="Partner actions"
                      >
                        <svg viewBox="0 0 16 16" fill="currentColor" width="16" height="16">
                          <path d="M8 5.25A1.625 1.625 0 108 2a1.625 1.625 0 000 3.25zM8 9.625a1.625 1.625 0 100-3.25 1.625 1.625 0 000 3.25zM9.625 12.375a1.625 1.625 0 11-3.25 0 1.625 1.625 0 013.25 0z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        <ListPaginationBar
          page={pagination.page}
          setPage={(n) => setPagination((p) => ({ ...p, page: n }))}
          total={pagination.total}
          pageSize={pagination.limit}
          totalPages={pagination.totalPages}
          ariaLabel="Admin partners pagination"
          className="px-3 py-3"
          style={{ borderTop: "1px solid #f3f4f6" }}
          loading={loading}
        />
      </div>

      {showModal ? (
        <Modal
          onClose={() => setShowModal(false)}
          title={editing ? "Edit partner" : "Add partner"}
          maxWidth="1100px"
          footer={(
            <>
              <button type="button" style={btnGhost} onClick={() => setShowModal(false)} disabled={saving}>
                Cancel
              </button>
              <button type="button" style={btnPrimary} onClick={handleSave} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label>
              Name *
              <input
                style={inputStyle}
                value={form.name}
                onChange={(e) => onNameChange(e.target.value)}
                required
              />
            </label>
            <label>
              Slug
              <input
                style={inputStyle}
                value={form.slug}
                onChange={(e) => {
                  setSlugLocked(true);
                  setForm({ ...form, slug: slugify(e.target.value, { maxLength: 64 }) });
                }}
                placeholder="auto from name"
              />
            </label>
            <label>
              Partner type
              <Select
                options={partnerTypeOptions}
                value={partnerTypeOptions.find((o) => o.value === Number(form.partner_type)) || null}
                onChange={(opt) => setForm({ ...form, partner_type: Number(opt?.value) || 1 })}
                styles={fieldSelectStyles}
                menuPortalTarget={document.body}
                isClearable={false}
                placeholder="Select partner type"
              />
            </label>
            <label>
              Stat label
              <input
                style={inputStyle}
                value={form.stat_label}
                onChange={(e) => setForm({ ...form, stat_label: e.target.value })}
                placeholder="Short card subtitle"
              />
            </label>
            <label>
              Partner network base URL
              <input
                style={inputStyle}
                value={form.base_url}
                onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                placeholder="https://strongmertics.gotrackier.com"
              />
            </label>

            <div
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={onLogoDrop}
              style={{
                gridColumn: "1 / -1",
                display: "flex",
                alignItems: "center",
                gap: 16,
                padding: 16,
                borderRadius: 12,
                border: "2px dashed #c7d2fe",
                background: "linear-gradient(180deg, #eef2ff 0%, #fff 100%)",
                cursor: "pointer",
              }}
            >
              <PartnerLogo partner={formPreviewPartner} size={64} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, color: "#3730a3" }}>
                  <MdCloudUpload size={22} />
                  {form.logo_url || logoPreview ? "Change logo" : "Upload partner logo"}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                  Drag & drop or click · JPEG, PNG, WebP · optional (initials used if empty)
                </div>
              </div>
              {(form.logo_url || logoPreview) ? (
                <button
                  type="button"
                  style={{ ...btnGhost, padding: 8 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    clearLogo();
                  }}
                  aria-label="Remove logo"
                >
                  <MdClose size={18} />
                </button>
              ) : null}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
              onChange={onLogoFileChange}
              style={{ display: "none" }}
            />

            <label style={{ gridColumn: "1 / -1" }}>
              Description
              <textarea
                style={textareaStyle}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </label>
            <label style={{ gridColumn: "1 / -1" }}>
              About text
              <textarea
                style={textareaStyle}
                value={form.about_text}
                onChange={(e) => setForm({ ...form, about_text: e.target.value })}
              />
            </label>

            <div style={sectionBox}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 600 }}>Partner fields</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    All params in one place — key, label, type and value. These appear in endpoint query-param multi-select below.
                  </div>
                </div>
                <button type="button" style={{ ...btnPrimary, padding: "8px 12px", fontSize: 13 }} onClick={addPartnerField}>
                  <MdAdd size={16} /> Add field
                </button>
              </div>
              {(form.partner_fields || []).length === 0 ? (
                <div style={{ fontSize: 13, color: "#9ca3af", padding: "8px 0" }}>
                  No fields yet. Add security_token, pub_id, source, install_goal_value, etc.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {(form.partner_fields || []).map((row, idx) => (
                    <div
                      key={`pf-${idx}`}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1.1fr 1.1fr 0.9fr 1.3fr auto",
                        gap: 8,
                        alignItems: "center",
                        background: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: 10,
                      }}
                    >
                      <input
                        style={inputStyle}
                        value={row.label}
                        onChange={(e) => updatePartnerField(idx, { label: e.target.value })}
                        placeholder="Label"
                        aria-label="Field label"
                      />
                      <input
                        style={inputStyle}
                        value={row.key}
                        onChange={(e) => updatePartnerField(idx, { key: e.target.value })}
                        placeholder="key"
                        aria-label="Field key"
                      />
                      <Select
                        options={PARTNER_CREDENTIAL_FIELD_TYPE}
                        value={
                          PARTNER_CREDENTIAL_FIELD_TYPE.find((o) => o.value === row.type) ||
                          PARTNER_CREDENTIAL_FIELD_TYPE[0]
                        }
                        onChange={(opt) => updatePartnerField(idx, { type: opt?.value || "text" })}
                        styles={fieldSelectStyles}
                        menuPortalTarget={document.body}
                        isClearable={false}
                        aria-label="Field type"
                      />
                      <input
                        style={inputStyle}
                        type={row.type === "secret" ? "password" : "text"}
                        value={row.value}
                        onChange={(e) => updatePartnerField(idx, { value: e.target.value })}
                        placeholder="Value (leave empty for runtime params like click_id)"
                        aria-label="Field value"
                      />
                      <button
                        type="button"
                        style={{
                          padding: 8,
                          background: "#fef2f2",
                          color: "#dc2626",
                          border: "1px solid #fecaca",
                          borderRadius: 8,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onClick={() => removePartnerField(idx)}
                        aria-label="Remove field"
                        title="Remove field"
                      >
                        <MdDelete size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={sectionBox}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 600 }}>Partner network endpoints</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    Path + multi-select params from Partner fields above. Keys <code>install</code> and <code>event</code> trigger postbacks.
                  </div>
                </div>
                <button type="button" style={{ ...btnPrimary, padding: "8px 12px", fontSize: 13 }} onClick={addEndpoint}>
                  <MdAdd size={16} /> Add endpoint
                </button>
              </div>
              {(form.default_config.endpoints || []).length === 0 ? (
                <div style={{ fontSize: 13, color: "#9ca3af", padding: "8px 0" }}>
                  No endpoints yet. Add click, install, and event paths.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {(form.default_config.endpoints || []).map((row, idx) => (
                    <div
                      key={`ep-${idx}`}
                      style={{
                        background: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: 10,
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 1fr 0.8fr auto",
                          gap: 8,
                          alignItems: "center",
                          marginBottom: 10,
                        }}
                      >
                        <input
                          style={inputStyle}
                          value={row.label}
                          onChange={(e) => updateEndpoint(idx, { label: e.target.value })}
                          placeholder="Label"
                          aria-label="Endpoint label"
                        />
                        <input
                          style={inputStyle}
                          value={row.key}
                          onChange={(e) => updateEndpoint(idx, { key: e.target.value })}
                          placeholder="key (install, event, click)"
                          aria-label="Endpoint key"
                        />
                        <input
                          style={inputStyle}
                          value={row.path}
                          onChange={(e) => updateEndpoint(idx, { path: e.target.value })}
                          placeholder="/acquisition"
                          aria-label="Endpoint path"
                        />
                        <Select
                          options={PARTNER_HTTP_METHOD}
                          value={
                            PARTNER_HTTP_METHOD.find((o) => o.value === row.http_method) ||
                            PARTNER_HTTP_METHOD[0]
                          }
                          onChange={(opt) =>
                            updateEndpoint(idx, {
                              http_method: opt?.value,
                              body_param_keys: opt?.value === "POST" ? row.body_param_keys || [] : [],
                            })
                          }
                          styles={fieldSelectStyles}
                          menuPortalTarget={document.body}
                          isClearable={false}
                          aria-label="HTTP method"
                        />
                        <button
                          type="button"
                          style={{
                            padding: 8,
                            background: "#fef2f2",
                            color: "#dc2626",
                            border: "1px solid #fecaca",
                            borderRadius: 8,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          onClick={() => removeEndpoint(idx)}
                          aria-label="Remove endpoint"
                          title="Remove endpoint"
                        >
                          <MdDelete size={18} />
                        </button>
                      </div>

                      <label style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
                        Query params
                        <Select
                          isMulti
                          options={fieldKeyOptions}
                          value={fieldKeyOptions.filter((o) => (row.param_keys || []).includes(o.value))}
                          onChange={(opts) =>
                            updateEndpoint(idx, { param_keys: (opts || []).map((o) => o.value) })
                          }
                          styles={multiSelectStyles}
                          menuPortalTarget={document.body}
                          placeholder={fieldKeyOptions.length ? "Select query params…" : "Add Partner fields first"}
                          isDisabled={!fieldKeyOptions.length}
                          aria-label="Endpoint query params"
                        />
                      </label>

                      {row.http_method === "POST" ? (
                        <label style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
                          Body params (POST)
                          <Select
                            isMulti
                            options={fieldKeyOptions}
                            value={fieldKeyOptions.filter((o) =>
                              (row.body_param_keys || []).includes(o.value)
                            )}
                            onChange={(opts) =>
                              updateEndpoint(idx, { body_param_keys: (opts || []).map((o) => o.value) })
                            }
                            styles={multiSelectStyles}
                            menuPortalTarget={document.body}
                            placeholder={fieldKeyOptions.length ? "Select body params…" : "Add Partner fields first"}
                            isDisabled={!fieldKeyOptions.length}
                            aria-label="Endpoint body params"
                          />
                        </label>
                      ) : null}

                      <div
                        style={{
                          fontSize: 12,
                          color: "#374151",
                          background: "#f3f4f6",
                          borderRadius: 6,
                          padding: "8px 10px",
                          wordBreak: "break-all",
                        }}
                      >
                        <span style={{ color: "#6b7280" }}>Preview URL: </span>
                        {buildEndpointPreviewUrl(form.base_url, row) || "Set base URL, path and params"}
                        {row.http_method === "POST" && buildEndpointPreviewBody(row) ? (
                          <>
                            <br />
                            <span style={{ color: "#6b7280" }}>Preview body: </span>
                            {buildEndpointPreviewBody(row)}
                          </>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <label style={{ gridColumn: "1 / -1", fontSize: 13, marginTop: 12, display: "block" }}>
                Partner events / goals (in-app mapping dropdown; comma-separated names or IDs)
                <input
                  style={inputStyle}
                  value={form.default_config.partner_events}
                  onChange={(e) => setDefaultConfigField("partner_events", e.target.value)}
                  placeholder="signup, purchase, booking"
                />
              </label>
            </div>
          </div>
        </Modal>
      ) : null}

      {openActionItem && dropdownPosition
        ? createPortal(
            <div
              ref={dropdownRef}
              style={{
                position: "fixed",
                left: dropdownPosition.left,
                top: dropdownPosition.top,
                zIndex: 10000,
                minWidth: "180px",
              }}
            >
              <div className="dropdown-menu-subscription">
                {getPartnerActionOptions(openActionItem, {
                  onEdit: openEdit,
                  onToggleStatus: handleToggleStatus,
                  onDelete: handleDelete,
                }).map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    className={opt.danger ? "delete-class" : ""}
                    onClick={() => handleDropdownAction(opt.action)}
                    style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                  >
                    <span style={{ marginRight: 8, display: "flex" }}>{opt.icon}</span>
                    <span style={{ fontSize: "0.875rem" }}>{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
